import type { Country_Iso2 } from '@shieldpay/steamhammer-ui/api';

/**
 * The returned validator function returns true if the value length is greater than
 * or equal to the given length, otherwise it returns false.
 */
export const createMinLength = (length: number) => (value: string | null) =>
  typeof value === 'string' && value.length >= length;

/**
 * The returned validator function returns true if the value length is less than or
 * equal to the given length, otherwise it returns false.
 */
export const createMaxLength = (length: number) => (value: string | null) =>
  typeof value !== 'string' || value.length <= length;

/**
 * The returned validator function returns true if the value length is
 * equal to the given length, otherwise it returns false.
 */
export const createIsLength = (length: number) => (value: string | null) =>
  value?.length === length;

/**
 * Returns true if the value contains an upper case character, otherwise false.
 * (ISO basic Latin alphabet)
 */
export const containsUpperCase = (value: string | null) =>
  typeof value === 'string' && /[A-Z]/.test(value);

/**
 * Returns true if the value contains a lower case character, otherwise false.
 * (ISO basic Latin alphabet)
 */
export const containsLowerCase = (value: string | null) =>
  typeof value === 'string' && /[a-z]/.test(value);

/**
 * Returns true if the value contains a number, otherwise false.
 */
export const containsNumber = (value: string | null) =>
  typeof value === 'string' && /\d/.test(value);

/**
 * Returns true if the value only contains digits, otherwise false.
 */
export const digitsOnly = (value: string | null) =>
  typeof value === 'string' && /^\d+$/.test(value);

/**
 * Returns true if the value only contains roman alphabet
 */
export const alphaOnly = (value: string | null) =>
  !!value && /^[a-z]+$/i.test(value);

/**
 * Returns true if the value contains a value, otherwise false.
 */
export const required = (value: string | null) => !!value?.trim();

/**
 * Returns true if the value only contains alphanumeric characters, otherwise false.
 */
export const alphaNumericOnly = (value: string | null) =>
  !!value && /^[a-z0-9]*$/i.test(value);

/**
 * Returns true if the value does not contain any of the specified or default specialChars, otherwise false.
 */
export const sanitisedString = (
  value: string | null,
  additionalSpecialChars: string = '',
) => {
  const defaultSpecialChars = '`/;\\-*|"\\\\';
  const specialChars = defaultSpecialChars + additionalSpecialChars;
  return !!value && !new RegExp(`[${specialChars}]`).test(value);
};

/**
 * Returns true if the value contains one of the following special characters:
 *
 * = + - ^ $ * . [ ] { } ( ) ? " ! @ # % & / \ , > < ' : ; | _ ~ `
 *
 * otherwise false.
 *
 * https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
 *
 */
const EMAIL_REGEX =
  // eslint-disable-next-line no-useless-escape
  /[\=\+\-\^\$\*\.\[\]\{\}\(\)\?\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]/;

export const containsSpecialCharacter = (value: string | null) =>
  typeof value === 'string' && EMAIL_REGEX.test(value);

/**
 * Returns true if the value does not contain whitespace, otherwise false.
 */
export const noWhitespace = (value: string | null) =>
  !!value && !value.includes(' ');

/**
 * Returns true if the value matches the specified currency format, otherwise false.
 *
 * The currency format can be either:
 * - Up to three digits, optionally followed by comma-separated groups of three digits, and an optional two decimal places.
 * - Any number of digits followed by an optional two decimal places.
 */
export const currencyValueValidator = (value: string | null) =>
  !!value &&
  /^[0-9]{1,3}(,[0-9]{3})*(\.[0-9]{1,2})?$|^([0-9]+(\.[0-9]{1,2})?)$/.test(
    value,
  );

/**
 * Returns true if the value matches bic/swift pattern, otherwise false.
 * This is based on Apply Financial's Bic/Swift validation format.
 */
export const bicSwiftValidator = (value: string | null) =>
  !!value && /^([0-9]{6}|^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?)$/i.test(value);

/**
 * Returns true if the value matches basic iban format, otherwise false.
 */
export const ibanValidator = (
  value: string | null,
  countryCodeIso2: Country_Iso2,
) => {
  if (value === null || value.length < 15 || value.length > 35) return false;

  if (!alphaNumericOnly(value)) return false;

  const [countryCode, checkDigits, BBAN] = [
    value.slice(0, 2),
    value.slice(2, 4),
    value.slice(4),
  ];

  if (countryCode.toUpperCase() !== countryCodeIso2.toUpperCase()) return false;
  if (!alphaOnly(countryCode)) return false;
  if (!digitsOnly(checkDigits)) return false;
  if (!alphaNumericOnly(BBAN)) return false;

  return true;
};
